import { pinNode, type Analysis, parseDeviceKey, STRUCT_KEYS } from './analyze';
import { LIB } from './library';
import { newId } from './tagging';
import type { Page, Placed, Project, Pt, Sym } from './types';
import { t as $t } from './i18n';


/*
 * Single-line overview derived from the multi-line schematic: for every motor
 * the feeder is traced upstream (overload, contactor, breaker, drive) and drawn
 * as one line from the busbar. Devices keep their tags, so every single-line
 * symbol cross-references its main function.
 */

const SL: Record<string, string> = { cb3: 'sl_cb', cb1: 'sl_cb', k3: 'sl_k', ol3: 'sl_ol', fuse: 'sl_fuse', vfd: 'sl_vfd', ss: 'sl_ss' };
const VFD_OUT = [5, 6, 7];

/** Feeder of one motor, from the busbar down: each level holds one device, or two for a reversing pair. */
export function traceFeeder(A: Analysis, motor: Placed): Placed[][] {
  const byNet = new Map<string, { it: Placed; i: number }[]>();
  for (const it of A.all)
    LIB[it.s.type].pins.forEach((_, i) => {
      const r = A.uf.find(pinNode(it.s, i));
      (byNet.get(r) || byNet.set(r, []).get(r)!).push({ it, i });
    });
  const levels: Placed[][] = [], seenNet = new Set<string>(), seenDev = new Set<string>();
  let front = [A.uf.find(pinNode(motor.s, 0))];
  for (let depth = 0; depth < 12 && front.length; depth++) {
    const next: string[] = [], level: Placed[] = [];
    for (const r of front) {
      if (seenNet.has(r)) continue;
      seenNet.add(r);
      const on = byNet.get(r) ?? [];
      if (on.some((x) => ['pot', 'brk'].includes(LIB[x.it.s.type].role))) continue; // reached a busbar
      for (const { it, i } of on) {
        const L = LIB[it.s.type];
        let from: number | undefined;
        if (it.s.type === 'vfd' && VFD_OUT.includes(i)) from = 0;
        else from = L.pass?.find(([, b]) => b === i)?.[0];
        if (from == null) continue;
        next.push(A.uf.find(pinNode(it.s, from)));
        const key = A.devKey.get(it.s.id) ?? it.s.id;
        if (SL[it.s.type] && !seenDev.has(key)) {
          seenDev.add(key);
          level.push(it);
        }
      }
    }
    if (level.length) levels.push(level);
    front = next;
  }
  return levels.reverse();
}

/** One or more single-line pages for the motors of the project (nine feeders per page). */
export function singleLinePages(pr: Project, A: Analysis): Page[] {
  const motors = A.all.filter((it) => it.s.type === 'motor3' || it.s.type === 'motor6').sort((a, b) => a.pi - b.pi || a.s.x - b.s.x);
  const pages: Page[] = [];
  const PER = 9;
  for (let k = 0; k < motors.length; k += PER) {
    const group = motors.slice(k, k + PER);
    const p: Page = { id: newId(), desc: $t("Tek hat şeması{0} · {1}", motors.length > PER ? ` ${k / PER + 1}` : '', group.map((m) => m.s.tag).join(', ')), kind: 'single', syms: [], wires: [] };
    const S = (type: string, x: number, y: number, src?: Placed, props: Sym['props'] = {}) => {
      const s: Sym = { id: newId(), type, x, y, rot: 0, tag: src?.s.tag ?? '', desc: '', part: '', maker: '', props };
      if (src) {
        // same function/location as the multi-line device, so the tags refer to the same device
        const k = parseDeviceKey(A.devKey.get(src.s.id) ?? '');
        if (k) for (const f of STRUCT_KEYS) if (k[f] !== (pr[f] || '')) s[f] = k[f];
      }
      p.syms.push(s);
    };
    const W = (pts: Pt[]) => p.wires.push({ id: newId(), pts, cs: '', col: '' });
    const x0 = 72, x1 = x0 + 40 * (group.length - 1);
    W([[40, 36], [Math.max(x1 + 24, 120), 36]]);
    S('text', 40, 31, undefined, { text: '3/N/PE ~ 400/230 V 50 Hz', size: 2.5 });
    group.forEach((m, i) => {
      const x = x0 + 40 * i;
      let y = 36;
      const levels = traceFeeder(A, m);
      // a six-terminal motor is started star-delta: its line contactor stands for the whole starter (IEC 60617 S00302)
      const sl = (it: Placed) => (m.s.type === 'motor6' && it.s.type === 'k3' ? 'sl_yd' : SL[it.s.type]);
      S('sl_3p', x, 42);
      for (const level of levels) {
        const h = (it: Placed) => (LIB[sl(it)].pins[1]?.[1] ?? 16);
        if (level.length === 1) {
          const it = level[0];
          W([[x, y], [x, y + 12]]);
          S(sl(it), x, y + 12, it);
          y += 12 + h(it);
        } else {
          const [a, b] = level;
          const hh = Math.max(h(a), h(b));
          W([[x, y], [x, y + 8]]);
          W([[x - 8, y + 8], [x + 8, y + 8]]);
          W([[x - 8, y + 8], [x - 8, y + 12]]);
          W([[x + 8, y + 8], [x + 8, y + 12]]);
          S(sl(a), x - 8, y + 12, a);
          S(sl(b), x + 8, y + 12, b);
          W([[x - 8, y + 12 + hh], [x - 8, y + 16 + hh], [x + 8, y + 16 + hh], [x + 8, y + 12 + hh]]);
          y += 16 + hh;
        }
      }
      W([[x, y], [x, y + 12]]);
      S('sl_m', x, y + 12, m);
    });
    pages.push(p);
  }
  return pages;
}
